"use client";

import { useEffect, useState, type CSSProperties } from "react";
import Link from "next/link";

import { useSession } from "@/lib/auth-client";
import { publicApi } from "@/lib/dpop-client";
import { cn } from "@/lib/utils";
import { fetchMarketSignalRows } from "@/hooks/use-market-data";
import { AssetIcon } from "@/components/shared/asset-icon";

interface TickerEvent {
  icon: string;
  text: string;
  accent: "green" | "blue" | "amber" | "purple" | "red";
  ticker?: string;
}

interface SignalRow {
  ticker: string | null;
  signal_date: string | null;
  direction: string | null;
  // API sends these as numeric strings ("0.9400", "2.000").
  confidence: number | string | null;
  stance: string | null;
  activity_multiplier: number | string | null;
  analysis_timestamp: string;
}

// Shown only until the first live payload lands — deliberately neutral so
// the pre-fetch state never reads as real signal data.
const FALLBACK_EVENTS: TickerEvent[] = [
  {
    icon: "●",
    text: "Connecting to the live signal tape…",
    accent: "blue",
  },
];

function formatRelative(timestamp: string): string {
  const diff = Date.now() - new Date(timestamp).getTime();
  const hours = Math.floor(diff / 3_600_000);
  const minutes = Math.floor(diff / 60_000);
  if (hours >= 1) return `${hours}h ago`;
  if (minutes >= 1) return `${minutes}m ago`;
  return "just now";
}

function signalToEvent(row: SignalRow): TickerEvent {
  const dirIcon =
    row.direction === "BUY" ? "▲" : row.direction === "SELL" ? "▼" : "→";
  const accent: TickerEvent["accent"] =
    row.stance === "bull" ? "green" : row.stance === "bear" ? "red" : "blue";
  const conf = Number(row.confidence);
  const pct = Number.isFinite(conf) ? (conf * 100).toFixed(1) : "—";
  const stanceLabel =
    row.stance === "bull"
      ? "Bullish"
      : row.stance === "bear"
        ? "Bearish"
        : "Neutral";
  const mult = Number(row.activity_multiplier);
  const activityLabel =
    Number.isFinite(mult) && mult >= 1.5
      ? ` · ${mult.toFixed(1)}x activity`
      : "";
  const conviction = conf >= 0.88 ? " · High conviction" : "";
  const time = formatRelative(row.analysis_timestamp);
  return {
    icon: dirIcon,
    text: `${row.direction} · ${pct}% · ${stanceLabel}${conviction}${activityLabel} · ${time}`,
    accent,
    ticker: row.ticker ?? undefined,
  };
}

interface TickRow {
  ticker: string | null;
  close: string | number | null;
  price_change_pct_24h: string | number | null;
}

function formatTapePrice(v: number): string {
  if (v >= 1000)
    return "$" + v.toLocaleString("en-US", { maximumFractionDigits: 0 });
  if (v >= 1)
    return (
      "$" +
      v.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );
  return "$" + v.toFixed(4);
}

function tickToEvent(row: TickRow): TickerEvent | null {
  const change = Number(row.price_change_pct_24h);
  const price = Number(row.close);
  if (
    !row.ticker ||
    !Number.isFinite(change) ||
    !Number.isFinite(price) ||
    price <= 0
  )
    return null;
  const up = change >= 0;
  return {
    icon: up ? "▲" : "▼",
    text: `${formatTapePrice(price)} · ${up ? "+" : ""}${change.toFixed(2)}% · 24h`,
    accent: up ? "green" : "red",
    ticker: row.ticker,
  };
}

const NUMERIC_TAPE_PART = /([+-]?(?:\d+(?:\.\d+)?|\.\d+)(?:[%x×])?)/g;
const IS_NUMERIC_TAPE_PART = /^[+-]?(?:\d+(?:\.\d+)?|\.\d+)(?:[%x×])?$/;

function TapeDetail({ text }: { text: string }) {
  return text.split(NUMERIC_TAPE_PART).map((part, index) =>
    IS_NUMERIC_TAPE_PART.test(part) ? (
      <span key={index} className="font-number text-foreground/75">
        {part}
      </span>
    ) : (
      part
    ),
  );
}

interface LiveActivityTickerProps {
  className?: string;
}

export function LiveActivityTicker({ className }: LiveActivityTickerProps) {
  const { data: session, status } = useSession();
  const [signalEvents, setSignalEvents] = useState<TickerEvent[]>([]);
  const [flashing, setFlashing] = useState(false);

  useEffect(() => {
    let cancelled = false;

    // The signals endpoint is auth-gated upstream; when it yields nothing,
    // fall back to the public ticks feed so the tape still shows real prices.
    const loadTicksFallback = async () => {
      const r = await publicApi.get(
        "/v1/analytics/market-data/ticks?limit=40&order=desc",
      );
      if (!r.ok) return;
      const json = await r.json();
      const rows: TickRow[] = Array.isArray(json)
        ? json
        : Array.isArray(json?.data)
          ? json.data
          : [];
      // Dedupe by ticker, keeping the row with the largest 24h move.
      const byTicker = new Map<string, TickRow>();
      for (const row of rows) {
        if (!row.ticker) continue;
        const move = Math.abs(Number(row.price_change_pct_24h));
        const existing = byTicker.get(row.ticker);
        const existingMove = existing
          ? Math.abs(Number(existing.price_change_pct_24h))
          : -1;
        if (Number.isFinite(move) && move >= existingMove) {
          byTicker.set(row.ticker, row);
        }
      }
      const events = [...byTicker.values()]
        .map(tickToEvent)
        .filter((e): e is TickerEvent => e !== null)
        .slice(0, 10);
      if (!cancelled && events.length > 0) setSignalEvents(events);
    };

    const loadSignals = async () => {
      // No public signals endpoint: signed-out visitors get public ticks.
      const rows = await fetchMarketSignalRows<SignalRow>(session);
      // Keep real market signals (must have a ticker + direction); ignore
      // tickerless metric rows (e.g. CHURN_PREDICTION). Dedupe to the latest
      // row per ticker, newest first.
      const latest = new Map<string, SignalRow>();
      for (const row of rows) {
        if (!row.ticker || !row.direction) continue;
        const existing = latest.get(row.ticker);
        const rowKey = row.signal_date ?? row.analysis_timestamp ?? "";
        const existingKey =
          existing?.signal_date ?? existing?.analysis_timestamp ?? "";
        if (!existing || rowKey > existingKey) {
          latest.set(row.ticker, row);
        }
      }
      const events = [...latest.values()]
        .sort((a, b) =>
          (b.analysis_timestamp ?? "").localeCompare(
            a.analysis_timestamp ?? "",
          ),
        )
        .slice(0, 10)
        .map(signalToEvent);
      if (cancelled) return;
      if (events.length > 0) setSignalEvents(events);
      else await loadTicksFallback();
    };

    if (status === "loading") return;
    loadSignals().catch(() => {
      loadTicksFallback().catch(() => {});
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, session?.fexrApiAccessToken]);

  const events = signalEvents.length > 0 ? signalEvents : FALLBACK_EVENTS;

  // Live signals landed: flash the rows once.
  useEffect(() => {
    if (signalEvents.length === 0) return;
    setFlashing(true);
    const timeout = setTimeout(() => setFlashing(false), 1600);
    return () => clearTimeout(timeout);
  }, [signalEvents]);

  const tickerStyle = {
    "--ticker-duration": `${Math.max(32, events.length * 6)}s`,
  } as CSSProperties;

  return (
    <div
      className={cn(
        "relative flex overflow-hidden border-y border-border bg-background/95 backdrop-blur-sm",
        className,
      )}
      aria-label="Live market signals"
    >
      <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-16 bg-gradient-to-r from-background to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-24 bg-gradient-to-l from-background to-transparent" />

      <div
        className="marketing-ticker-track flex w-max items-center gap-0 whitespace-nowrap py-2.5"
        style={tickerStyle}
      >
        {[...events, ...events].map((ev, i) => (
          <Link
            key={i}
            href="/dashboard/strategies"
            aria-label={`Open strategies${ev.ticker ? ` for ${ev.ticker}` : ""}`}
            className={cn(
              "inline-flex items-center gap-2 px-5 font-small text-[0.75rem] tabular-nums outline-none transition-colors hover:text-foreground focus-visible:bg-primary/10 focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-primary/50",
              flashing &&
                (ev.accent === "red" ? "market-flash-down" : "market-flash-up"),
            )}
            style={
              flashing
                ? { animationDelay: `${(i % events.length) * 45}ms` }
                : undefined
            }
          >
            <span
              className={cn(
                "text-[0.75rem] leading-none",
                ev.accent === "green" && "text-success",
                ev.accent === "red" && "text-danger",
                ev.accent === "blue" && "text-blue-400",
                ev.accent === "amber" && "text-warning",
                ev.accent === "purple" && "text-primary",
              )}
            >
              {ev.icon}
            </span>
            <span className="inline-flex items-center gap-1.5 text-muted-foreground">
              {ev.ticker && <AssetIcon symbol={ev.ticker} size={22} />}
              {ev.ticker && (
                <span className="font-heading font-semibold text-foreground/80">
                  {ev.ticker}/USD
                </span>
              )}
              <span>·</span>
              <span className="font-small">
                <TapeDetail text={ev.text} />
              </span>
            </span>
            <span className="mx-2 text-border">·</span>
          </Link>
        ))}
      </div>
    </div>
  );
}
